import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from '../ui/use-toast';
import {
  Plus,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  CheckCircle,
  Loader2,
  Truck
} from 'lucide-react';
import { AdminTransporterAPI, TransporterCreateRequest } from '../../lib/api';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';

interface TransporterCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TransporterCreateModal({
  isOpen,
  onClose,
  onSuccess
}: TransporterCreateModalProps) {
  // Form state
  const [formData, setFormData] = useState<TransporterCreateRequest>({
    name: '',
    email: '',
    password: '',
    mobile: '',
    alternatePhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    drivingLicenseNumber: '',
    drivingLicenseExpiry: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form data
  const updateFormData = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    if (!formData.address.street.trim()) newErrors['address.street'] = 'Street address is required';
    if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
    if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required';
    if (!formData.address.pincode.trim()) newErrors['address.pincode'] = 'Pincode is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile validation
    const mobileRegex = /^[6-9]\d{9}$/;
    if (formData.mobile && !mobileRegex.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await AdminTransporterAPI.createTransporter(formData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Transporter created successfully",
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to create transporter",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating transporter:', error);
      toast({
        title: "Error",
        description: "Failed to create transporter. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      name: '',
      email: '',
      password: '',
      mobile: '',
      alternatePhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      drivingLicenseNumber: '',
      drivingLicenseExpiry: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="2xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Add New Transporter
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Create a new transporter account with basic information and contact details
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            {/* Personal Information */}
            <FormSection
              title="Personal Information"
              icon={<User className="h-5 w-5" />}
              description="Basic personal details of the transporter"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Full Name"
                  required
                  error={errors.name}
                >
                  <Input
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="Enter full name"
                  />
                </FormField>

                <FormField
                  label="Email Address"
                  required
                  error={errors.email}
                >
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="Enter email address"
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={3}>
                <FormField
                  label="Mobile Number"
                  required
                  error={errors.mobile}
                >
                  <Input
                    value={formData.mobile}
                    onChange={(e) => updateFormData('mobile', e.target.value)}
                    placeholder="Enter mobile number"
                  />
                </FormField>

                <FormField
                  label="Alternate Phone"
                  hint="Optional alternate contact number"
                >
                  <Input
                    value={formData.alternatePhone}
                    onChange={(e) => updateFormData('alternatePhone', e.target.value)}
                    placeholder="Enter alternate phone"
                  />
                </FormField>

                <FormField
                  label="Password"
                  required
                  error={errors.password}
                >
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    placeholder="Enter password"
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Address Information */}
            <FormSection
              title="Address Information"
              icon={<MapPin className="h-5 w-5" />}
              description="Complete address details"
            >
              <FormField
                label="Street Address"
                required
                error={errors['address.street']}
              >
                <Input
                  value={formData.address.street}
                  onChange={(e) => updateFormData('address.street', e.target.value)}
                  placeholder="Enter street address"
                />
              </FormField>

              <FormGrid columns={2}>
                <FormField
                  label="City"
                  required
                  error={errors['address.city']}
                >
                  <Input
                    value={formData.address.city}
                    onChange={(e) => updateFormData('address.city', e.target.value)}
                    placeholder="Enter city"
                  />
                </FormField>

                <FormField
                  label="State"
                  required
                  error={errors['address.state']}
                >
                  <Input
                    value={formData.address.state}
                    onChange={(e) => updateFormData('address.state', e.target.value)}
                    placeholder="Enter state"
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={2}>
                <FormField
                  label="Pincode"
                  required
                  error={errors['address.pincode']}
                >
                  <Input
                    value={formData.address.pincode}
                    onChange={(e) => updateFormData('address.pincode', e.target.value)}
                    placeholder="Enter pincode"
                  />
                </FormField>

                <FormField
                  label="Country"
                  required
                >
                  <Input
                    value={formData.address.country}
                    onChange={(e) => updateFormData('address.country', e.target.value)}
                    placeholder="Enter country"
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* License Information */}
            <FormSection
              title="License Information"
              icon={<FileText className="h-5 w-5" />}
              description="Driving license details (optional)"
              collapsible
              defaultExpanded={false}
            >
              <FormGrid columns={2}>
                <FormField
                  label="Driving License Number"
                  hint="Optional driving license number"
                >
                  <Input
                    value={formData.drivingLicenseNumber}
                    onChange={(e) => updateFormData('drivingLicenseNumber', e.target.value)}
                    placeholder="Enter license number"
                  />
                </FormField>

                <FormField
                  label="License Expiry Date"
                  hint="Optional license expiry date"
                >
                  <Input
                    type="date"
                    value={formData.drivingLicenseExpiry}
                    onChange={(e) => updateFormData('drivingLicenseExpiry', e.target.value)}
                  />
                </FormField>
              </FormGrid>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Transporter
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}